<template>
  <div class="step-card">
    <div class="step-content">
      <!-- Title Section -->
      <div class="title-section">
        <h2 class="step-title">How did you find out about us?</h2>
        <p class="step-subtitle">This helps us improve our reach (optional)</p>
      </div>

      <!-- Discovery Source Input -->
      <div class="input-container">
        <label class="input-label">Discovery Source</label>
        <SingleSelect
          v-model="selectedSource"
          :options="discoveryOptionsFormatted"
          placeholder="Select an option..."
          variant="bordered"
          @change="handleSourceChange"
          teleport
          matchTriggerWidth="true"
        />

        <!-- Other Input Field (shown when "Other" is selected) -->
        <div v-if="showOtherInput" class="other-input-container">
          <label class="input-label" for="discovery-other-input"
            >Please specify</label
          >
          <input
            id="discovery-other-input"
            v-model="otherSourceText"
            type="text"
            class="other-input"
            placeholder="Tell us more..."
            @input="handleOtherInputChange"
          />
        </div>
      </div>

      <!-- Button Container -->
      <div class="button-container">
        <ActionButton @click="handleBack" variant="outline" class="back-button"
          >Back</ActionButton
        >
        <div class="right-buttons">
          <ActionButton @click="handleSkip" variant="text" class="skip-button"
            >Skip</ActionButton
          >
          <ActionButton @click="handleNext" class="next-button"
            >Next</ActionButton
          >
        </div>
      </div>
    </div>
  </div>
  <ActionButton @click="handleSkip" variant="text" class="skip-button-mobile"
    >Skip</ActionButton
  >
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from "vue";
import ActionButton from "../common/ActionButton.vue";
import SingleSelect from "../common/SingleSelect.vue";

const props = defineProps({
  modelValue: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["next", "back", "skip", "update:modelValue"]);

const discoveryOptions = ref([
  { id: 1, label: "Social Media" },
  { id: 2, label: "Search Engine" },
  { id: 3, label: "Friend or Colleague" },
  { id: 4, label: "Email Newsletter" },
  { id: 5, label: "Blog or Article" },
  { id: 6, label: "Conference or Event" },
  { id: 7, label: "Other" },
]);

const discoveryOptionsFormatted = computed(() => {
  return discoveryOptions.value.map((option) => ({
    value: String(option.id),
    text: option.label,
  }));
});

const selectedSource = ref(
  props.modelValue?.sourceId ? String(props.modelValue.sourceId) : ""
);
const otherSourceText = ref(props.modelValue?.otherText || "");

const OTHER_OPTION_ID = 7;

const showOtherInput = computed(() => {
  return selectedSource.value === String(OTHER_OPTION_ID);
});

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue) {
      selectedSource.value = newValue.sourceId ? String(newValue.sourceId) : "";
      otherSourceText.value = newValue.otherText || "";
    } else {
      selectedSource.value = "";
      otherSourceText.value = "";
    }
  },
  { immediate: true }
);

const handleSourceChange = (event) => {
  const sourceId = event?.value
    ? parseInt(event.value)
    : parseInt(selectedSource.value);
  selectedSource.value = sourceId ? String(sourceId) : "";

  if (sourceId !== OTHER_OPTION_ID) {
    otherSourceText.value = "";
  }
  emitUpdate();
};

const handleOtherInputChange = () => {
  emitUpdate();
};

const emitUpdate = () => {
  const sourceId = selectedSource.value ? parseInt(selectedSource.value) : null;
  const value = sourceId
    ? {
        sourceId: sourceId,
        otherText: showOtherInput.value ? otherSourceText.value : null,
      }
    : null;
  emit("update:modelValue", value);
};

const handleNext = () => {
  const sourceId = selectedSource.value ? parseInt(selectedSource.value) : null;
  const data = {
    discoverySource: sourceId
      ? {
          sourceId: sourceId,
          otherText: showOtherInput.value ? otherSourceText.value : null,
        }
      : null,
  };
  emit("next", data);
};

const handleBack = () => {
  emit("back");
};

const handleSkip = () => {
  emit("skip");
};
</script>

<style scoped>
.step-card {
  background-color: white;
  border: 1px solid #0478ce;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 32px;
  border-radius: 16px;
  width: 100%;
}

.step-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: flex-end;
  width: 100%;
}

.title-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  text-align: center;
  color: #3b4648;
}

.step-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-xxl);
  line-height: 36px;
  color: #3b4648;
  margin: 0;
  width: 100%;
}

.step-subtitle {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.024px;
  color: #3b4648;
  margin: 0;
  width: 100%;
}

.input-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.input-label {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #3b4648;
  margin: 0;
  text-align: left;
}

.other-input-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.other-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d8dae5;
  border-radius: 8px;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #3b4648;
  background-color: white;
  box-sizing: border-box;
  transition: border-color 0.3s ease;
}

.other-input:focus {
  outline: none;
  border-color: #0478ce;
}

.other-input::placeholder {
  color: #6c737f;
}

.button-container {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.right-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
}

.back-button {
  background-color: white;
  border: 1px solid #d8dae5;
  box-sizing: border-box;
  display: flex;
  gap: 8px;
  height: 40px;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 8px;
  min-width: 100px;
  cursor: pointer;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-small);
  line-height: 20px;
  color: #3b4648;
  white-space: nowrap;
  letter-spacing: 0.014px;
  transition: all 0.3s ease;
}

.back-button:hover {
  background-color: #f2f3fb;
  border-color: #0478ce;
}

.skip-button {
  width: 100px;
  background-color: #f2f3fb;
  border: none;
  box-sizing: border-box;
  display: flex;
  gap: 8px;
  height: 40px;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: var(--fs-small);
  line-height: 20px;
  color: var(--primary-blue);
  white-space: nowrap;
  letter-spacing: 0.014px;
  transition: all 0.3s ease;
}

.skip-button:hover {
  color: #3b4648;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 61, 106, 0.3);
}

.next-button {
  width: 100px;
  height: 40px;
  padding: 10px 16px;
  border-radius: 8px;
  font-weight: 700;
  font-size: var(--fs-small);
  line-height: 20px;
  white-space: nowrap;
  letter-spacing: 0.014px;
  transition: all 0.3s ease;
}

.skip-button-mobile {
  display: none;
}

@media (max-width: 768px) {
  .step-card {
    min-height: auto;
  }

  .button-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .right-buttons {
    width: 100%;
  }

  .back-button {
    margin-bottom: 0px;
  }

  .skip-button {
    display: none;
  }

  .skip-button-mobile {
    margin-top: 10px;
    display: block;
  }

  .back-button,
  .skip-button,
  .next-button {
    flex: 1;
  }
}
</style>
